/**
 * D12 · one finding, three places. The readiness panel, the operation inspector, and the
 * pre-flight queue render the *same component* — only `placement` differs.
 *
 * **The UI never composes an explanation of its own.** Every word here comes from the check:
 *
 *     BLOCKING   1 aseptic operation covered by no environmental monitoring      ← label
 *     WHY        The operation is marked aseptic, so the room must be            ← why
 *                monitored throughout it.
 *     EXPECTED   Every aseptic operation lies between a span's anchors.          ← expected
 *     ACTUAL     No span covers it.                                              ← actual
 *     WHERE      Sterile filtration · operation-level                            ← where
 *                [Extend the monitoring span]                                    ← fix
 *
 * If a check cannot say why, it is not ready to block (D0 §6). A finding the surface had to
 * explain would be a finding the surface invented, and an invented reason is the one thing an
 * audit cannot accept.
 *
 * `dimension` comes from the check too, so the queue's grouping cannot drift from the checks
 * it is grouping.
 *
 * **The standard is collapsed by default, not dropped.** What blocks and where it is are the
 * two things a reader acts on, so those stay open; why · expected · actual are one click
 * behind them. Eight findings each holding six lines pushed the record itself off the screen,
 * and a panel nobody can see past is a panel nobody reads. Every word is still the check's
 * own — this changes when they are shown, never whether they exist or who wrote them.
 */
import React, { useState } from "react";
import { Button, Flex, Typography } from "antd";

const { Text } = Typography;

/**
 * How many places a finding names, and which steps. The server lists at most eight places in
 * `where` (enough to read) and states the real count in `where_total` and every step in
 * `nodes`. Counting or marking from `where` stopped at eight: a record with 30 unnamed steps
 * read "8" and its ninth step carried no mark.
 */
export const findingCount = (f) => f?.where_total ?? ((f?.where || []).length || 1);
export const findingNodes = (f) => f?.nodes ?? (f?.where || []).map((w) => w.node).filter(Boolean);
export const findingTouches = (f, code) => !!code && findingNodes(f).includes(code);

export default function Finding({
  finding,
  placement = "panel",   // "inline" · "panel" · "queue"
  onGoTo,
  onFix,
  whereLimit,   // how many places to list before "and n more" — served, not a literal here
}) {
  const [open, setOpen] = useState(false);
  if (!finding) return null;
  const blocking = finding.severity === "blocking";
  const where = finding.where || [];
  const listed = whereLimit ? where.slice(0, whereLimit) : where;
  const more = findingCount(finding) - listed.length;
  const standard = [
    ["Why", finding.why?.text],
    ["Expected", finding.expected?.text],
    ["Actual", finding.actual?.text],
  ].filter(([, text]) => text);

  // On a row there is no space for the full standard, and no need: the row IS the where.
  if (placement === "inline") {
    return (
      <span className={`u0-find-inline ${blocking ? "" : "advisory"}`}>
        ✕ {finding.label?.text}
      </span>
    );
  }

  return (
    <div className={`u0-find ${blocking ? "" : "advisory"} ${placement}`}>
      <Flex justify="space-between" align="baseline" gap={8}>
        <span className="sev">{finding.severity}</span>
        <Flex gap={6} align="baseline">
          {finding.dimension ? <span className="dim">{finding.dimension}</span> : null}
          <span className="dz-code">{finding.id}</span>
        </Flex>
      </Flex>

      <div className="what">{finding.label?.text}</div>

      {standard.length ? (
        <>
          <button
            className="u0-find-more"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? "Hide why" : "Why"}
          </button>
          {open
            ? standard.map(([label, text]) => (
                <div key={label} className="kv"><b>{label}</b><span>{text}</span></div>
              ))
            : null}
        </>
      ) : null}

      {where.length ? (
        <div className="kv">
          <b>Where</b>
          <span>
            {listed.map((w, i) => (
              <button
                key={`${w.node || w.operation || i}`}
                className="u0-where"
                onClick={() => onGoTo?.(w)}
                disabled={!onGoTo}
              >
                {w.label || w.node || w.operation}
              </button>
            ))}
            {more > 0 ? (
              <Text type="secondary" style={{ fontSize: "calc(11px * var(--dz-fs, 1))" }}>
                {" "}and {more} more
              </Text>
            ) : null}
          </span>
        </div>
      ) : null}

      {/* A fix the record can perform is performed. The label and the action come from the
          check — `fix` is `{label, action}`, and rendering the object itself is what crashed
          the first version of this component. The surface never writes the label. */}
      {finding.fix?.label && onFix ? (
        <Button size="small" onClick={() => onFix(finding)} style={{ marginTop: 6 }}>
          {finding.fix.label}
        </Button>
      ) : finding.fix?.label ? (
        <Text type="secondary" style={{ fontSize: "calc(11px * var(--dz-fs, 1))", marginTop: 4, display: "block" }}>
          {finding.fix.label}
        </Text>
      ) : null}
    </div>
  );
}
